import { ReactElement, useEffect, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CellComponent from "../cellComponent";
import { DashboardSchemaType, IAcquisitionCell, ICell, IDashboard, IDashboardResult } from "../../types";
import { DateTime } from "luxon";
import { IDictionary, ISelectedNode, IState, Modules } from "../../../../types";
import { useSelector } from "react-redux";
import { useAsyncFetch, useFetch } from "../../../../system/useFetch";
import { getEmptyDashboard } from "../utils";
import { cloneObject } from "../../../../system/utils";

export interface IChartDialogProps {
  from: DateTime;
  to: DateTime;
  cell: IAcquisitionCell;
  onCancel: () => void;
}

const ChartDialog = ({ onCancel, cell, from, to }: IChartDialogProps): ReactElement => {
  const selectedNode = useSelector<IState, ISelectedNode>(state => state.selectedNode);
  const [dashboard, setDashboard] = useState<IDashboard>(getEmptyDashboard());
  const [maximized, setMaximized] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [cellData, setCellData] = useState<IDictionary<any>>({});
  const [getDashboardData] = useAsyncFetch<IDashboardResult>();
  const [ schemas, setSchemas ] = useState<Record<DashboardSchemaType, any> | null>(null);
  const { data: dashboardData } = useFetch<Record<DashboardSchemaType, any> | null>(
    {
      module: "dashboard",
      query: "getSchemaConfig",
      payload: {
        nodeID: selectedNode.nodeID,
        nodeType: selectedNode.type
      }
    }
  );

  useEffect(() => {
    if (dashboardData) {
      setSchemas(dashboardData);
    }

    const newCellData: Record<string, any> = {};
    setLoading(true);

    getDashboardData({
      module: "dashboard",
      query: "getDashboardData",
      payload: {
        nodeID: selectedNode.nodeID,
        nodeType: selectedNode.type,
        moduleID: Modules.INTERRUPTIONS,
        from: from.toFormat("yyyy-MM-dd HH:mm:ss"),
        to: to.toFormat("yyyy-MM-dd HH:mm:ss"),
        cells: [cell]
      }
    }).then(request => {
      if (!request) {
        return;
      }

      newCellData[cell.key] = request[cell.key];

      const newDashboard = cloneObject(dashboard);
      newDashboard.name = "Quality Round";
      newDashboard.layout.rows = [ [ cell ] ];

      setDashboard(newDashboard);
      setCellData({ ...newCellData });
      setLoading(false);
    });
  }, [dashboardData, cell, from, to ]);

  const setSelectedCell = (selected: ICell | IAcquisitionCell | null) => {
    setMaximized(selected !== null);
  };

  const onUpdatePalette = (p: IDictionary<string>, hasChange = false) => {
    if (hasChange){
      setDashboard({ ...dashboard, layout: { ...dashboard.layout, palette: p } });
    }
  };

  const parentSize = {
    width: 1000,
    height: 600
  };

  return (
    <Dialog onClose={onCancel} className="unselectable" open={true} maxWidth="lg">
      <DialogContent dividers={true}>

        {loading && !schemas &&
          <div style={{ width: parentSize.width, height: 100 }} className="display-flex-row justify-content-center align-content-center">
            <CircularProgress className="mr-1" size={22} />
          </div>
        }

        {!loading && schemas &&
          <CellComponent
            cell={cell}
            responseData={cellData}
            loading={loading}
            parentSize={parentSize}
            dashboard={dashboard}
            setDashboard={setDashboard}
            setSelectedCell={setSelectedCell}
            isMaximized={maximized}
            schemas={schemas}
            onUpdatePalette={onUpdatePalette}
            disableSettings={true}/>
        }

      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="contained" color="secondary" startIcon={<CloseIcon/>}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChartDialog;
